import { CustomDialog } from "@/components/Dialog";
import { Form, Modal, Spinner } from "react-bootstrap";
import { Input } from "@/components/Input";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/store";
import { getEarningOptions$ } from "@/store/references";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createSubAccount$ } from "@/store/sub_accounts";
import { useTranslations } from "use-intl";
import { toast } from "react-toastify";

const ERROR_CODE: any = {
  4014: "wallet",
  4015: "invalidName",
  5010: "unexpected",
  4013: "alreadyExist",
  4005: "verification",
  4011: "currency",
  4010: "earningScheme",
  4012: "alreadyExist",
};

export function AddSubAccountDialog({ onClose }: { onClose: () => void }) {
  const [error, setError] = useState("");
  const [show, setShow] = useState(true);
  const i18n = useTranslations("cabinet");
  const references = useSelector((state: RootState) => state.references);
  const dispatch = useAppDispatch();

  const formik = useFormik({
    initialValues: {
      earning_scheme_type: "",
      crypto_currency: "",
      wallet_address: "",
      name: "",
    },
    validationSchema: Yup.object({
      earning_scheme_type: Yup.string().required("Заполните это поле"),
      crypto_currency: Yup.string().required("Заполните это поле"),
      wallet_address: Yup.string().required("Заполните это поле"),
      name: Yup.string().required("Заполните это поле"),
    }),
    validateOnChange: false,
    onSubmit: (values, formikHelpers) => {
      formikHelpers.setSubmitting(true);
      dispatch(createSubAccount$(formik.values as any)).then((e: any) => {
        formikHelpers.setSubmitting(false);
        if (e?.payload?.status == 202) {
          toast.success("Instructions sent to email");
        } else if (e?.payload?.status == 201) {
          toast.success("Request to create a subaccount accepted");
        } else {
          if (e?.error) {
            if (e.error?.message && ERROR_CODE[e.error?.message]) {
              setError(`addSubAccount.form.${ERROR_CODE[e.error?.message]}`);
            } else {
              setError(`addSubAccount.form.verification`);
            }
            return;
          } else if (!e?.payload?.wallet_address) {
            toast.success("instructions sent to email");
          }
        }
        setShow(false);
      });
    },
  });

  useEffect(() => {
    dispatch(getEarningOptions$());
  }, []);

  useEffect(() => {
    let timeout: any;
    if (!show) {
      timeout = setTimeout(() => {
        onClose();
      }, 100);
    }
    return () => clearTimeout(timeout);
  }, [show]);

  const filteredCurrencies = Array.from(
    new Set(
      references.earningOptions
        .filter((o) => o.earning_scheme_type === formik.values.earning_scheme_type)
        .map((o) => o.currency),
    ),
  );

  return (
    <CustomDialog
      onClose={() => setShow(false)}
      show={show}
      body={
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          {/* 1. Name — always visible */}
          <Input
            placeholder={i18n("addSubAccount.name")}
            name={"name"}
            onChange={formik.handleChange}
            value={formik.values.name}
            error={formik.errors.name}
          />
          <div
            className={"text-muted small mb-3"}
            style={{ marginTop: "-0.75rem" }}
          >
            {i18n("addSubAccount.nameHint")}
          </div>

          {/* 2. Earning scheme — always visible, starts empty */}
          <Form.Select
            value={formik.values.earning_scheme_type}
            name={"earning_scheme_type"}
            className={"form--control form-control mb-3 p-2"}
            size={"sm"}
            onChange={(e) => {
              formik.setFieldValue("earning_scheme_type", e.target.value);
              formik.setFieldValue("crypto_currency", "");
              formik.setFieldValue("wallet_address", "");
            }}
          >
            <option value="">—</option>
            {references.earningSchemes.map((scheme, idx) => (
              <option value={scheme} key={idx}>
                {scheme}
              </option>
            ))}
          </Form.Select>

          {/* 3. Currency — appears only after scheme is selected, starts empty */}
          {formik.values.earning_scheme_type && (
            <Form.Select
              value={formik.values.crypto_currency}
              name={"crypto_currency"}
              className={"form--control form-control mb-3 p-2"}
              size={"sm"}
              onChange={(e) => {
                formik.setFieldValue("crypto_currency", e.target.value);
                formik.setFieldValue("wallet_address", "");
              }}
            >
              <option value="">—</option>
              {filteredCurrencies.map((cur, idx) => (
                <option value={cur} key={idx}>
                  {cur}
                </option>
              ))}
            </Form.Select>
          )}

          {/* 4. Wallet — appears only after currency is selected */}
          {formik.values.crypto_currency && (
            <Input
              placeholder={i18n("addSubAccount.wallet")}
              name={"wallet_address"}
              onChange={formik.handleChange}
              value={formik.values.wallet_address}
              error={formik.errors.wallet_address}
            />
          )}

          {error && (
            <div className={"btn-danger text-danger mt-4"}>{i18n(error)}</div>
          )}

          <div className={"d-flex gap-2 mt-3"}>
            {formik.isSubmitting ? (
              <Spinner animation={"grow"} />
            ) : (
              <button
                className={"btn btn-sm p-2 btn-primary"}
                type={"submit"}
              >
                {i18n("addSubAccount.btn")}
              </button>
            )}
          </div>
        </form>
      }
      heading={<Modal.Title>{i18n("addSubAccount.step2.title")}</Modal.Title>}
    />
  );
}
